import { useState, useRef, useEffect, ReactNode, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'

interface InfoPopoverProps {
  children: ReactNode
  /** Additional classes for the info icon */
  iconClassName?: string
  /** Additional classes for the popover content */
  contentClassName?: string
  /** Side to show the popover */
  side?: 'top' | 'bottom' | 'left' | 'right'
}

/**
 * Info icon that shows a popover on click/tap.
 * Uses a portal to avoid clipping by parent containers.
 */
export function InfoPopover({
  children,
  iconClassName,
  contentClassName,
  side = 'bottom',
}: InfoPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Calculate popover position based on trigger element
  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const popoverRect = popoverRef.current.getBoundingClientRect()
    const padding = 8

    let top = 0
    let left = 0

    switch (side) {
      case 'top':
        top = triggerRect.top - popoverRect.height - padding
        left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2
        break
      case 'bottom':
        top = triggerRect.bottom + padding
        left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2
        break
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2
        left = triggerRect.left - popoverRect.width - padding
        break
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2
        left = triggerRect.right + padding
        break
    }

    // Keep within viewport bounds
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    if (left < padding) left = padding
    if (left + popoverRect.width > viewportWidth - padding) {
      left = viewportWidth - popoverRect.width - padding
    }
    if (top < padding) top = padding
    if (top + popoverRect.height > viewportHeight - padding) {
      top = viewportHeight - popoverRect.height - padding
    }

    setPosition({ top, left })
  }, [side])

  // Update position when opened
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure popover is rendered before measuring
      requestAnimationFrame(() => {
        updatePosition()
      })
    }
  }, [isOpen, updatePosition])

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    // Use a small delay to prevent immediate close on the same tap
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }, 10)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen])

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm',
          iconClassName
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
            }}
            className={cn(
              'z-[100] overflow-hidden rounded-md border bg-popover p-3 text-sm text-popover-foreground shadow-md',
              'animate-in fade-in-0 zoom-in-95',
              contentClassName
            )}
            role="tooltip"
          >
            {children}
          </div>,
          document.body
        )}
    </>
  )
}

export default InfoPopover
